// ============================================================================
// FSA ENGAGEMENT ENDPOINTS
// ============================================================================
// Backend controller: FsaEngagementsController → /api/v1/FsaEngagements
// FSA-2.1 surface — per-engagement FSA settings.

import { buildEndpoint, buildEndpointWithParams } from '../base.endpoints';
import type { ID } from '../../types';

const BASE_PATH = '/FsaEngagements';

export const fsaEngagementEndpoints = {
  /** GET /api/v1/FsaEngagements — paged list of engagements with FSA configured. */
  list: () => BASE_PATH,

  /** GET /api/v1/FsaEngagements/eligible — paged list of engagements that do NOT yet have FSA configured. */
  eligible: () => `${BASE_PATH}/eligible`,

  /** GET /api/v1/FsaEngagements/{engagementId}/settings */
  getSettings: (engagementId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:engagementId/settings`, { engagementId }),

  /** POST /api/v1/FsaEngagements/{engagementId}/settings */
  createSettings: (engagementId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:engagementId/settings`, { engagementId }),

  /** PUT /api/v1/FsaEngagements/{engagementId}/settings/{id} */
  updateSettings: (engagementId: ID, id: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:engagementId/settings/:id`, { engagementId, id }),

  // ----- FSA-2.3 Roll Forward -----

  /** GET /api/v1/FsaEngagements/{priorEngagementId}/roll-forward-spec
   *  Pre-fills the Roll Forward confirm dialog with period dates + carried
   *  settings from the prior engagement. */
  getRollForwardSpec: (priorEngagementId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:priorEngagementId/roll-forward-spec`, { priorEngagementId }),

  /** GET /api/v1/FsaEngagements/{priorEngagementId}/duplicate-check
   *  Returns data when a next-year engagement already exists; null otherwise. */
  duplicateCheck: (priorEngagementId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:priorEngagementId/duplicate-check`, { priorEngagementId }),

  /** POST /api/v1/FsaEngagements/create
   *  Creates a new basic-tier engagement + FSA settings + applies the
   *  carry-forward toggles. Shared by Roll Forward + Create New paths.
   *  Note: wrapped in buildEndpoint() so the /v1 version prefix is added —
   *  static-path endpoints don't go through buildEndpointWithQuery and
   *  would otherwise hit /api/FsaEngagements/create (404). */
  create: () => buildEndpoint(`${BASE_PATH}/create`),

  /** GET /api/v1/FsaEngagements/{engagementId}/opening-balance-discrepancies
   *  Reconciliation card data. */
  getOpeningBalanceDiscrepancies: (engagementId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:engagementId/opening-balance-discrepancies`, { engagementId }),

  /** Raw path (no version prefix) for buildEndpointWithQuery */
  basePath: () => BASE_PATH,
} as const;

export default fsaEngagementEndpoints;
